import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { billingAPI } from "../../lib/api";
import { formatAUD } from "../../lib/billing";
import { TrendingUp, TrendingDown, DollarSign, Wallet, ChevronDown, ChevronUp } from "lucide-react";

function Stat({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-slate-200" data-testid={testId}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0 ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-bold text-slate-900 truncate">{value}</p>
      </div>
    </div>
  );
}

export function CompanyProfitOverview({ refreshKey, onSelectCustomer }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await billingAPI.getOverview();
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!data || data.profiles_count === 0) return null;

  const profitPositive = data.monthly_profit >= 0;

  return (
    <Card className="border-l-4 border-l-emerald-500" data-testid="company-profit-overview">
      <CardContent className="pt-5 pb-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold text-slate-700">
            Company Overview <span className="text-slate-400 font-normal">— {data.profiles_count} billing profile{data.profiles_count !== 1 ? "s" : ""}</span>
          </p>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} data-testid="profit-overview-toggle">
            {expanded ? <><ChevronUp className="w-4 h-4 mr-1" /> Hide breakdown</> : <><ChevronDown className="w-4 h-4 mr-1" /> Per-customer breakdown</>}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={DollarSign} label="Monthly Revenue" value={formatAUD(data.monthly_revenue)} accent="bg-blue-600" testId="overview-stat-revenue" />
          <Stat icon={Wallet} label="Monthly Costs (OTS)" value={formatAUD(data.monthly_cost)} accent="bg-slate-500" testId="overview-stat-cost" />
          <Stat icon={profitPositive ? TrendingUp : TrendingDown} label="Monthly Profit" value={formatAUD(data.monthly_profit)} accent={profitPositive ? "bg-emerald-600" : "bg-red-600"} testId="overview-stat-profit" />
          <Stat icon={TrendingUp} label="Annual Profit" value={formatAUD(data.annual_profit)} accent="bg-indigo-600" testId="overview-stat-annual" />
        </div>
        {expanded && (
          <div className="border rounded-lg overflow-hidden" data-testid="profit-breakdown-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Customer</th>
                  <th className="px-3 py-2 font-medium text-right">Revenue /mo</th>
                  <th className="px-3 py-2 font-medium text-right">Cost /mo</th>
                  <th className="px-3 py-2 font-medium text-right">Profit /mo</th>
                </tr>
              </thead>
              <tbody>
                {data.customers.map((c) => (
                  <tr
                    key={c.customer_id}
                    className="border-t border-slate-100 hover:bg-blue-50 cursor-pointer"
                    onClick={() => onSelectCustomer?.(c.customer_id)}
                    data-testid={`profit-row-${c.customer_id}`}
                  >
                    <td className="px-3 py-2 text-slate-800">{c.customer_name}</td>
                    <td className="px-3 py-2 text-right">{formatAUD(c.monthly_revenue)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{formatAUD(c.monthly_cost)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${c.monthly_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {formatAUD(c.monthly_profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
